import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  AlertDialogSlide,
  SingleSelect,
  Textfield,
} from "widgets";
import {
  useFetchCountryList,
  useFetchUdiseSchool,
  useSearchSchools,
} from "store/backend/apiHooks";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import routes from "constants/routes";
import {
  DEFAULT_SCHOOL_PAGE_NUMBER,
  DEFAULT_SCHOOL_PAGE_SIZE,
  sanitizeUdiseCode,
  SCHOOL_API_DEFAULT_ERROR_MESSAGE,
  UDISE_CODE_PATTERN,
} from "constants/schoolConstants";
import {
  buildCreateSchoolPrefill,
  getCountryOptions,
  getSelectedCountryOption,
  hasRegisteredSchoolForUdise,
  isIndiaCountry,
  normalizeFormId,
} from "helpers/schoolUtils";
import { ZERO, SEARCH_VALIDATION_MESSAGES } from "constants/constants";

const CreateSchoolDialog = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { openSnackbar } = useSnackbarContext();
  const { data, isPending, isError } = useFetchCountryList(open);
  const {
    mutateAsync: fetchUdiseSchool,
    isPending: isUdiseLookupPending,
  } = useFetchUdiseSchool();
  const {
    mutateAsync: searchSchools,
    isPending: isUdiseSearchPending,
  } = useSearchSchools();
  const countryOptions = useMemo(
    () => getCountryOptions(data?.items ?? []),
    [data?.items],
  );
  const [countryId, setCountryId] = useState("");
  const [udiseCode, setUdiseCode] = useState("");
  const [udiseCodeError, setUdiseCodeError] = useState("");

  const selectedCountry = getSelectedCountryOption(countryOptions, countryId);
  const isIndia = isIndiaCountry(selectedCountry?.label);
  const hasCountrySelected = Boolean(selectedCountry);
  const trimmedUdise = udiseCode.trim();
  const isUdiseValid = UDISE_CODE_PATTERN.test(trimmedUdise);
  const isLookupPending = isUdiseLookupPending || isUdiseSearchPending;
  const showUdiseFormatError =
    isIndia && trimmedUdise.length > ZERO && !isUdiseValid;
  const showUdiseError = showUdiseFormatError || Boolean(udiseCodeError);
  const canCreate =
    hasCountrySelected &&
    (!isIndia || isUdiseValid) &&
    !isPending &&
    !isError &&
    !isLookupPending;

  const resetFields = () => {
    setCountryId("");
    setUdiseCode("");
    setUdiseCodeError("");
  };

  const handleClose = () => {
    resetFields();
    onClose?.();
  };

  const navigateToCreateSchool = (state) => {
    navigate(routes.createSchool.createSchool, { state });
    handleClose();
  };

  const handleCountryChange = (event) => {
    const nextCountryId = normalizeFormId(event.target.value);
    setCountryId(nextCountryId);
    setUdiseCodeError("");
    const nextIsIndia = isIndiaCountry(
      getSelectedCountryOption(countryOptions, nextCountryId)?.label,
    );
    if (!nextIsIndia) {
      setUdiseCode("");
    }
  };

  const handleUdiseChange = (event) => {
    setUdiseCodeError("");
    setUdiseCode(sanitizeUdiseCode(event.target.value));
  };

  const fetchMasterUdiseSchool = async () => {
    try {
      const udiseSchool = await fetchUdiseSchool(trimmedUdise);
      if (udiseSchool?.statusCode === 200) return udiseSchool;
      openSnackbar(SCHOOL_API_DEFAULT_ERROR_MESSAGE, "error");
      return null;
    } catch (error) {
      if (error?.statusCode === 404) {
        setUdiseCodeError(
          "We couldn't find this UDISE Code. Please enter a valid UDISE Code to continue.",
        );
        return null;
      }
      openSnackbar(SCHOOL_API_DEFAULT_ERROR_MESSAGE, "error");
      return null;
    }
  };

  const isUdiseFreeToRegister = async () => {
    try {
      const existingSchools = await searchSchools({
        udiseCode: trimmedUdise,
        page: DEFAULT_SCHOOL_PAGE_NUMBER,
        pageSize: DEFAULT_SCHOOL_PAGE_SIZE,
      });
      if (existingSchools?.statusCode !== 200) {
        openSnackbar(SCHOOL_API_DEFAULT_ERROR_MESSAGE, "error");
        return false;
      }
      if (hasRegisteredSchoolForUdise(existingSchools)) {
        setUdiseCodeError("A school already exists for this UDISE Code.");
        return false;
      }
      return true;
    } catch {
      openSnackbar(SCHOOL_API_DEFAULT_ERROR_MESSAGE, "error");
      return false;
    }
  };

  const handleCreate = async () => {
    if (!canCreate) return;

    if (!isIndia) {
      navigateToCreateSchool(
        buildCreateSchoolPrefill({ countryId, udiseCode: "" }),
      );
      return;
    }

    const udiseSchool = await fetchMasterUdiseSchool();
    if (!udiseSchool) return;

    const canRegister = await isUdiseFreeToRegister();
    if (!canRegister) return;

    navigateToCreateSchool(
      buildCreateSchoolPrefill({
        countryId,
        udiseCode: trimmedUdise,
        udiseSchool,
      }),
    );
  };

  return (
    <AlertDialogSlide
      open={open}
      onClose={handleClose}
      title="Create School"
      actionLabel={isLookupPending ? "Loading..." : "Create School"}
      onAction={handleCreate}
      actionDisabled={!canCreate}
      disableClose={isLookupPending}
      cancelLabel="Cancel"
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem", minWidth: 320, paddingTop: 8 }}>
        <SingleSelect
          tag="Country *"
          menuItems={countryOptions}
          value={countryId}
          handleChange={handleCountryChange}
          disabled={isPending || isError || isLookupPending}
          errorMessage={
            isError ? "Failed to load countries. Please try again." : undefined
          }
        />
        {isIndia ? (
          <Textfield
            label="UDISE code *"
            value={udiseCode}
            handleOnChange={handleUdiseChange}
            error={showUdiseError}
            helperText={
              showUdiseFormatError
                ? SEARCH_VALIDATION_MESSAGES.UDISE_INVALID
                : udiseCodeError || undefined
            }
            disabled={isLookupPending}
          />
        ) : null}
      </div>
    </AlertDialogSlide>
  );
};

export default CreateSchoolDialog;
